import { paymentStatusInfo, paymentTypeLabel, paymentMethodColor } from "@/services/payment/appointmentPaymentStatus";
import { AppointmentStatus, MerchantAppointmentQueueDto } from '@/models/club';
import { Badge, Card, Divider, Group, SimpleGrid, Stack, Text, Title } from '@mantine/core';
import { IconCalendar, IconCheck, IconClock, IconX } from '@tabler/icons-react';
import moment from 'moment-jalaali';
import PaymentReference from './PaymentReference';

type Props = {
    appointment: MerchantAppointmentQueueDto | null;
}

function AppointmentDetail({ appointment }: Props) {
    const status = appointment?.status === AppointmentStatus.Value0 ? "رزرو شده" : appointment?.status === AppointmentStatus.Value1 ? "نهایی شده" : appointment?.status === AppointmentStatus.Value2 ? "لغو شده" : appointment?.status === AppointmentStatus.Value3 ? "پرداخت ناموفق" : appointment?.status === AppointmentStatus.Value4 ? "بازگشت داده شده" : "نامشخص";
    const money = (value?: number | null) => typeof value === "number" ? `${value.toLocaleString("fa-IR")} ریال` : "ـ";

    if (!appointment) return <Text c="dimmed">اطلاعات نوبت در دسترس نیست.</Text>;

    const payment = paymentStatusInfo(appointment);
    return <Stack gap="md">
        <Card withBorder>
            <Group justify="space-between" align="flex-start">
                <Stack gap={2}><Text fw={800}>{appointment.customer?.firstName} {appointment.customer?.lastName}</Text><Text size="xs" c="dimmed">{appointment.customer?.phoneNumber || "شماره تماس ثبت نشده"}</Text></Stack>
                <Stack gap={2} align="flex-end"><Badge color={payment.color} size="lg" variant="light">{payment.label}</Badge><Text size="xs" c="dimmed">{payment.description}</Text></Stack>
            </Group>
        </Card>
        <SimpleGrid cols={{ base: 2, sm: 4 }}>
            <Card withBorder p="sm"><Text size="xs" c="dimmed">روش پرداخت</Text><Badge mt={4} size="sm" variant="light" color={paymentMethodColor(appointment)}>{paymentTypeLabel(appointment)}</Badge></Card>
            <Card withBorder p="sm"><Text size="xs" c="dimmed">وضعیت نوبت</Text><Text size="sm" fw={750}>{status}</Text></Card>
            <Card withBorder p="sm"><Text size="xs" c="dimmed">مبلغ پرداختی مشتری</Text><Text size="sm" fw={750}>{money(appointment.feePayer != null || (appointment.payableAmount ?? 0) > 0 ? appointment.payableAmount : appointment.rateBreakdown?.totalPrice)}</Text></Card>
            {(appointment.discountAmount ?? 0) > 0 && <Card withBorder p="sm" bg="green.0"><Text size="xs" c="green.8">تخفیف{appointment.discountCode ? ` (${appointment.discountCode})` : ""}</Text><Text size="sm" fw={750} c="green.9">−{money(appointment.discountAmount)}</Text></Card>}
            {(appointment.customerFeeAmount ?? 0) > 0 && <Card withBorder p="sm"><Text size="xs" c="dimmed">کارمزد مشتری</Text><Text size="sm" fw={750}>{money(appointment.customerFeeAmount)}</Text></Card>}
            {(appointment.merchantFeeAmount ?? 0) > 0 && <Card withBorder p="sm"><Text size="xs" c="dimmed">کارمزد سهم شما</Text><Text size="sm" fw={750} c="red.7">{money(appointment.merchantFeeAmount)}</Text></Card>}
            {(appointment.rateBreakdown?.markupAmount ?? 0) > 0 && <Card withBorder p="sm"><Text size="xs" c="dimmed">کارمزد پدیکس</Text><Text size="sm" fw={750}>{money(appointment.rateBreakdown?.markupAmount)}</Text></Card>}
        </SimpleGrid>
        <SimpleGrid cols={{ base: 1, sm: 3 }}>
            <Card withBorder p="sm"><Text size="xs" c="dimmed">تاریخ ثبت رزرو</Text><Text>{appointment.creationTime ? moment(appointment.creationTime).format("jYYYY/jMM/jDD - HH:mm") : "—"}</Text></Card>
            <Card withBorder p="sm"><Text size="xs" c="dimmed">زمان پرداخت درگاه</Text><Text>{appointment.paymentInfo?.paymentDate ? moment(appointment.paymentInfo.paymentDate).format("jYYYY/jMM/jDD - HH:mm") : "—"}</Text></Card>
            <Card withBorder p="sm"><Text size="xs" c="dimmed">شماره پیگیری</Text><Group justify="flex-start"><PaymentReference value={appointment.paymentInfo?.rrn} /></Group></Card>
        </SimpleGrid>
        <Stack gap="xs"><Title order={5}>خدمات نوبت</Title>
        {appointment.appointments?.map((item, index) => (
            <Card withBorder key={item.appointmentId ?? index}>
                    <Stack>
                        <Stack gap="5">
                            <Group justify='space-between'>
                                <Text>
                                    خدمت :
                                </Text>
                                <Text>
                                    {item.merchantServiceTitle}
                                </Text>
                            </Group>
                            <Group justify='space-between'>
                                <Text>
                                    متخصص :
                                </Text>
                                <Text>
                                    {item.merchantUserFirstName} {item.merchantUserLastName}
                                </Text>
                            </Group>

                        </Stack>
                        <Divider />
                        <Group justify='space-between'>
                            <Group gap="5" align='center' justify='center'>
                                <IconCalendar stroke={1} size={20} />
                                <Text>
                                    {moment(item.appointmentDate).format("jYYYY/jMM/jDD")}
                                </Text>
                            </Group>
                            <Group gap="5" align='center' justify='center'>
                                <IconClock stroke={1} size={20} />
                                <Text>
                                    {moment(item.startTime, "HH:mm:ss").format("HH:mm")} - {moment(item.endTime, "HH:mm:ss").format("HH:mm")}
                                </Text>
                            </Group>
                        </Group>

                    </Stack>
            </Card>
        ))}</Stack>
        <Card withBorder>
            <Group justify="space-between"><Title order={5}>تسویه</Title><Badge color={appointment.hasSettlement ? "green" : "yellow"} variant="light" leftSection={appointment.hasSettlement ? <IconCheck size={13} /> : <IconX size={13} />}>{appointment.hasSettlement ? "تسویه شده" : "تسویه نشده"}</Badge></Group>
            {appointment.hasSettlement && <><Divider my="sm" /><SimpleGrid cols={{ base: 1, sm: 3 }}><Stack gap={2}><Text size="xs" c="dimmed">مبلغ تسویه</Text><Text size="sm" fw={700}>{money(appointment.settlement?.amount)}</Text></Stack><Stack gap={2}><Text size="xs" c="dimmed">تاریخ تسویه</Text><Text size="sm">{appointment.settlement?.settleDate ? moment(appointment.settlement.settleDate).format("jYYYY/jMM/jDD, HH:mm") : "ـ"}</Text></Stack><Stack gap={2}><Text size="xs" c="dimmed">شماره پیگیری</Text><Text size="sm">{appointment.settlement?.rrn || "ـ"}</Text></Stack></SimpleGrid></>}
        </Card>
    </Stack>;
}

export default AppointmentDetail;
